import React from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendUp } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const effTone = (v) =>
  v >= 100 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : v >= 75 ? "bg-amber-100 text-amber-700 border-amber-200"
      : "bg-red-100 text-red-700 border-red-200";

const Stat = ({ label, value, sub, testId }) => (
  <div className="min-w-0">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-lg font-bold text-slate-900 mt-0.5" data-testid={testId}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5 truncate">{sub}</div>}
  </div>
);

export default function MyPerformance() {
  const { data } = useQuery({
    queryKey: ["my-performance"],
    queryFn: async () => (await api.get("/work/my-performance")).data,
    refetchInterval: 120000,
  });

  if (!data) return null;
  const { today, month } = data;

  return (
    <Card className="p-5 border-slate-200 shadow-none" data-testid="my-performance">
      <div className="flex items-center gap-2 mb-4">
        <TrendUp className="h-5 w-5 text-blue-600" weight="fill" />
        <h3 className="font-semibold text-slate-900">My Performance</h3>
        <Badge variant="outline" className={`ml-auto ${effTone(month.efficiency)}`} data-testid="my-eff-month">
          {month.efficiency}% this month
        </Badge>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Today" value={`${today.actual_hours} h`}
          sub={`${today.planned_hours} h planned · ${today.entries} entr${today.entries === 1 ? "y" : "ies"}`}
          testId="my-today-hours" />
        <Stat label="Today's efficiency" value={`${today.efficiency}%`}
          sub={today.actual_hours ? "planned ÷ actual" : "no hours logged yet"} testId="my-today-eff" />
        <Stat label="This month" value={`${month.actual_hours} h`}
          sub={`${month.planned_hours} h planned · ${month.jobs} job${month.jobs === 1 ? "" : "s"}`}
          testId="my-month-hours" />
        <Stat label="Output / approvals" value={`${month.output}`}
          sub={data.pending_approval ? `${data.pending_approval} awaiting approval` : "nothing pending"}
          testId="my-month-output" />
      </div>
    </Card>
  );
}
